import React from 'react';

import { QRCodeSVG } from 'qrcode.react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, CircularProgress, Collapse, Link, Typography, useTheme } from '@mui/material';

import { buildPortalSecretUrl } from '@/utils/url/url';

// Vite only exposes variables prefixed VITE_. Without it these read undefined, the links get
// href="", and clicking one reloads the secret instead of opening the page.
const PRIVACY_POLICY_URL = (import.meta.env.VITE_PRIVACY_POLICY_URL || '').trim();
const PORTAL_URL = (import.meta.env.VITE_PORTAL_URL || '').trim();

interface ViewSecretProps {
    onView: () => Promise<void>;
    isLoading?: boolean;
    privacyPolicyUrl?: string;
    portalUrl?: string;
    /** The whole id (both halves, see parseRevealUrl), so the portal link opens this secret. Never the key. */
    secretId?: string;
}

export const ViewSecret = ({
    onView,
    isLoading = false,
    privacyPolicyUrl = PRIVACY_POLICY_URL || '',
    portalUrl = PORTAL_URL || '',
    secretId,
}: ViewSecretProps) => {
    const theme = useTheme();
    const [error, setError] = React.useState<string | null>(null);

    const [showQRCode, setShowQRCode] = React.useState(false);

    const currentUrl = window.location.href;
    const portalSecretUrl = buildPortalSecretUrl(portalUrl, secretId);

    const handleView = async () => {
        setError(null);

        try {
            await onView();
        } catch (_err) {
            setError('Failed to load secret. Please try again.');
        }
    };

    const handleOpenInAnotherDevice = () => {
        setShowQRCode((prev: boolean) => !prev);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                p: 3,
                maxWidth: 400,
                mx: 'auto',
            }}
        >
            {/* View button */}
            <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleView}
                disabled={isLoading}
                sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                }}
            >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'View secret'}
            </Button>

            {/* Open in Another Device */}
            <Button
                variant="outlined"
                fullWidth
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenInAnotherDevice}
                sx={{
                    py: 1.5,
                    fontWeight: 600,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: theme.palette.primary.main + '08',
                    },
                }}
            >
                Open on another device
            </Button>

            {/* QR Code Section */}
            <Collapse in={showQRCode} timeout="auto">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        mt: 2,
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.primary,
                            textAlign: 'center',
                        }}
                    >
                        Scan this QR code to open this same page in another device.
                    </Typography>

                    <Box
                        sx={{
                            p: 2,
                            bgcolor: '#FFFFFF',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <QRCodeSVG value={currentUrl} size={200} level="M" bgColor="#FFFFFF" fgColor="#000000" />
                    </Box>
                </Box>
            </Collapse>

            {/* Error message */}
            {error && (
                <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                    {error}
                </Typography>
            )}

            {/* Privacy Policy — hidden rather than shown with an empty href if the URL is missing */}
            {privacyPolicyUrl && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        By clicking View secret, you agree to our{' '}
                        <Link
                            href={privacyPolicyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Privacy Policy
                        </Link>
                    </Typography>
                </Box>
            )}

            {/* Portal Link — same. No referrer: this page's address is the link itself, key included. */}
            {portalSecretUrl && (
                <Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Owner of the secret?{' '}
                        <Link
                            href={portalSecretUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            referrerPolicy="no-referrer"
                            sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Find it in your portal
                        </Link>
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
